<template>
  <div class="pdv-dash">

    <!-- ── Header ─────────────────────────────────────────────────────────── -->


    <!-- ── Cards de resumo ────────────────────────────────────────────────── -->
    <div class="pdv-dash__cards">

      <!-- Skeleton enquanto carrega -->
      <template v-if="carregando">
        <div v-for="n in 3" :key="'sk-' + n" class="pdv-dash__card pdv-dash__card--skeleton">
          <div class="pdv-dash__skeleton-icon"></div>
          <div class="pdv-dash__skeleton-lines">
            <div class="pdv-dash__skeleton-line pdv-dash__skeleton-line--sm"></div>
            <div class="pdv-dash__skeleton-line pdv-dash__skeleton-line--lg"></div>
            <div class="pdv-dash__skeleton-line pdv-dash__skeleton-line--sm"></div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="pdv-dash__card">
          <div class="pdv-dash__card-icon pdv-dash__card-icon--blue">
            <i class="fas fa-box"></i>
          </div>
          <div class="pdv-dash__card-info">
            <span class="pdv-dash__card-label">Produtos sincronizados</span>
            <span class="pdv-dash__card-value">{{ resumo.produtos_total | numero }}</span>
            <span class="pdv-dash__card-sub">Produtos disponíveis no terminal</span>
          </div>
        </div>

        <div class="pdv-dash__card">
          <div class="pdv-dash__card-icon pdv-dash__card-icon--green">
            <i class="fas fa-users"></i>
          </div>
          <div class="pdv-dash__card-info">
            <span class="pdv-dash__card-label">Clientes sincronizados</span>
            <span class="pdv-dash__card-value">{{ resumo.clientes_total | numero }}</span>
            <span class="pdv-dash__card-sub">Clientes disponíveis offline</span>
          </div>
        </div>

        <div class="pdv-dash__card">
          <div class="pdv-dash__card-icon pdv-dash__card-icon--indigo">
            <i class="fas fa-file-invoice-dollar"></i>
          </div>
          <div class="pdv-dash__card-info">
            <span class="pdv-dash__card-label">Vendas concluídas</span>
            <span class="pdv-dash__card-value">{{ resumo.nfces_total | numero }}</span>
            <span class="pdv-dash__card-sub">Autorizadas ou finalizadas neste terminal</span>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Atalhos rápidos ────────────────────────────────────────────────── -->
    <div class="pdv-dash__atalhos">
      <button class="pdv-dash__atalho pdv-dash__atalho--primary" @click="irParaNovaVenda">
        <i class="fas fa-plus-circle"></i>
        <span>Nova venda</span>
      </button>

      <button class="pdv-dash__atalho pdv-dash__atalho--secondary" @click="irParaVendas">
        <i class="fas fa-list-alt"></i>
        <span>Vendas</span>
      </button>

      <button
        class="pdv-dash__atalho pdv-dash__atalho--outline"
        :disabled="sincronizando || !online"
        @click="sincronizarAgora"
      >
        <i :class="sincronizando ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
        <span>{{ sincronizando ? 'Sincronizando...' : 'Sincronizar' }}</span>
      </button>
    </div>

    <!-- ── Barra de progresso de sync ────────────────────────────────────── -->
    <div v-if="sincronizando" class="pdv-dash__sync-bar">
      <div class="progress" style="height: 6px; border-radius: 4px;">
        <div
          class="progress-bar progress-bar-striped progress-bar-animated"
          role="progressbar"
          :style="{ width: progressoSync + '%' }"
        ></div>
      </div>
      <small class="pdv-dash__sync-msg">{{ statusSync }}</small>
    </div>

    <!-- ── Feedback ───────────────────────────────────────────────────────── -->
    <transition name="pdv-fade">
      <div v-if="msgSucesso" class="pdv-dash__alert pdv-dash__alert--success">
        <i class="fas fa-check-circle mr-2"></i>{{ msgSucesso }}
      </div>
    </transition>
    <transition name="pdv-fade">
      <div v-if="msgErro" class="pdv-dash__alert pdv-dash__alert--danger">
        <i class="fas fa-exclamation-triangle mr-2"></i>{{ msgErro }}
      </div>
    </transition>

    <!-- ── Linha de conteúdo: gráfico + status ────────────────────────────── -->
    <div class="pdv-dash__content">

      <!-- Gráfico de produtos mais vendidos -->
      <div class="pdv-dash__panel">
        <div class="pdv-dash__panel-header">
          <i class="fas fa-chart-pie mr-2 text-primary"></i>
          <span>Produtos mais vendidos na semana</span>
        </div>
        <div class="pdv-dash__panel-body">
          <template v-if="carregandoGrafico">
            <div class="pdv-dash__chart-placeholder">
              <span class="spinner-border text-primary"></span>
            </div>
          </template>
          <template v-else-if="produtosMaisVendidos.length">
            <apexchart
              type="donut"
              height="280"
              :options="chartOptions"
              :series="chartSeries"
            />
          </template>
          <div v-else class="pdv-dash__empty">
            <i class="fas fa-chart-pie fa-2x mb-2 text-muted"></i>
            <p>Nenhuma venda registrada nesta semana.</p>
          </div>
        </div>
      </div>

      <!-- Gráfico de grupos/categorias -->
      <div class="pdv-dash__panel">
        <div class="pdv-dash__panel-header">
          <i class="fas fa-tags mr-2 text-primary"></i>
          <span>Vendas por grupo/categoria na semana</span>
        </div>
        <div class="pdv-dash__panel-body">
          <template v-if="carregandoGrafico">
            <div class="pdv-dash__chart-placeholder">
              <span class="spinner-border text-primary"></span>
            </div>
          </template>
          <template v-else-if="vendasPorGrupo.length">
            <apexchart
              type="bar"
              height="280"
              :options="chartOptionsGrupos"
              :series="chartSeriesGrupos"
            />
          </template>
          <div v-else class="pdv-dash__empty">
            <i class="fas fa-tags fa-2x mb-2 text-muted"></i>
            <p>Sem dados de grupos/categorias para o período.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ Modal Configurações ════════════════════════════════════════════ -->
    <div v-if="modalSettings" class="pdv-dash__overlay" @click.self="modalSettings = false">
      <div class="pdv-dash__modal">

        <div class="pdv-dash__modal-header">
          <div>
            <h4 class="pdv-dash__modal-title">
              <i class="fas fa-cog mr-2"></i>Configurações do Terminal
            </h4>
            <small class="text-muted">Dados locais do PDV Desktop</small>
          </div>
          <button class="pdv-dash__btn-close" @click="modalSettings = false">✕</button>
        </div>

        <div class="pdv-dash__modal-body">

          <!-- Dados do terminal -->
          <div class="pdv-dash__settings-section">
            <p class="pdv-dash__settings-section-title">Dados do Terminal</p>
            <table class="pdv-dash__settings-table">
              <tr>
                <td>UUID do terminal</td>
                <td><code>{{ terminal.uuid_terminal || '—' }}</code></td>
              </tr>
              <tr>
                <td>Nome</td>
                <td>{{ terminal.nome || '—' }}</td>
              </tr>
              <tr>
                <td>Terminal</td>
                <td>{{ terminal.nome || terminal.id_terminal_venda || '—' }}</td>
              </tr>
              <tr>
                <td>Empresa (ID)</td>
                <td>{{ terminal.id_empresa || '—' }}</td>
              </tr>
              <tr>
                <td>Status</td>
                <td>
                  <span :class="['pdv-dash__tag', online ? 'pdv-dash__tag--green' : 'pdv-dash__tag--red']">
                    {{ online ? 'Online' : 'Offline' }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>Última sincronização</td>
                <td>{{ _formatarData(resumo.ultima_sincronizacao) }}</td>
              </tr>
              <tr>
                <td>Última validação</td>
                <td>{{ _formatarData(terminal.terminal_validado_em || terminal.terminal_ultimo_acesso_em) }}</td>
              </tr>
              <tr>
                <td>Vendas pendentes</td>
                <td>
                  <span :class="['pdv-dash__tag', resumo.vendas_pendentes > 0 ? 'pdv-dash__tag--orange' : 'pdv-dash__tag--neutral']">
                    {{ resumo.vendas_pendentes }}
                  </span>
                </td>
              </tr>
              <tr>
                <td>Caixa pendente</td>
                <td>
                  <span :class="['pdv-dash__tag', resumo.caixa_pendente > 0 ? 'pdv-dash__tag--orange' : 'pdv-dash__tag--neutral']">
                    {{ resumo.caixa_pendente > 0 ? 'Sim' : 'Não' }}
                  </span>
                </td>
              </tr>
            </table>
          </div>

          <!-- Configuração NFC-e -->
          <div class="pdv-dash__settings-section">
            <p class="pdv-dash__settings-section-title">Configuração NFC-e</p>
            <table class="pdv-dash__settings-table">
              <tr>
                <td>Descrição</td>
                <td>{{ dadosTerminal.configuracao ? _nomeConfiguracaoNfce(dadosTerminal.configuracao) : '—' }}</td>
              </tr>
              <tr>
                <td>Modelo</td>
                <td>{{ (dadosTerminal.configuracao && dadosTerminal.configuracao.modelo) || '—' }}</td>
              </tr>
              <tr>
                <td>Série</td>
                <td>{{ (dadosTerminal.configuracao && dadosTerminal.configuracao.serie) || terminal.serie_nfe || '—' }}</td>
              </tr>
              <tr>
                <td>Próxima nota</td>
                <td>#{{ (dadosTerminal.configuracao && dadosTerminal.configuracao.proximaNota) || '—' }}</td>
              </tr>
              <tr>
                <td>Ambiente</td>
                <td>
                  <span v-if="dadosTerminal.configuracao && dadosTerminal.configuracao.ambiente == 1" class="pdv-dash__tag pdv-dash__tag--red">Produção</span>
                  <span v-else-if="dadosTerminal.configuracao && dadosTerminal.configuracao.ambiente == 2" class="pdv-dash__tag pdv-dash__tag--orange">Homologação</span>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </table>
          </div>

          <!-- Impressora -->
          <div class="pdv-dash__settings-section">
            <p class="pdv-dash__settings-section-title">Impressora</p>
            <table class="pdv-dash__settings-table">
              <tr>
                <td>Impressora padrão</td>
                <td>{{ (dadosTerminal.impressora && dadosTerminal.impressora.printer_name) || 'Padrão do sistema' }}</td>
              </tr>
              <tr>
                <td>Tipo</td>
                <td>{{ (dadosTerminal.impressora && dadosTerminal.impressora.tipo) || '—' }}</td>
              </tr>
              <tr>
                <td>Impressão automática</td>
                <td>{{ (dadosTerminal.impressora && dadosTerminal.impressora.impressao_automatica) ? 'Sim' : 'Não' }}</td>
              </tr>
            </table>
          </div>

        </div>

        <div class="pdv-dash__modal-footer">
          <button
            class="pdv-dash__modal-btn pdv-dash__modal-btn--outline"
            @click="_recarregarTerminal"
          >
            <i class="fas fa-redo mr-1"></i>Recarregar dados
          </button>
          <button
            class="pdv-dash__modal-btn pdv-dash__modal-btn--outline"
            :disabled="!online || sincronizandoFiscal"
            @click="_sincronizarFiscal"
          >
            <span v-if="sincronizandoFiscal">
              <span class="spinner-border spinner-border-sm mr-1"></span>Sincronizando...
            </span>
            <span v-else><i class="fas fa-file-invoice-dollar mr-1"></i>Sincronizar fiscal</span>
          </button>
          <button
            class="pdv-dash__modal-btn pdv-dash__modal-btn--outline"
            :disabled="fazendoBackup"
            @click="_fazerBackup"
          >
            <span v-if="fazendoBackup">
              <span class="spinner-border spinner-border-sm mr-1"></span>Fazendo backup...
            </span>
            <span v-else><i class="fas fa-database mr-1"></i>Fazer backup agora</span>
          </button>
          <button
            class="pdv-dash__modal-btn pdv-dash__modal-btn--primary"
            @click="irParaImpressora"
          >
            <i class="fas fa-print mr-1"></i>Configurar impressora
          </button>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import SincronizacaoService          from "@/services/SincronizacaoService";
import FiscalContextPdvDesktopService from "../services/FiscalContextPdvDesktopService";
import DashboardPdvDesktopService    from "../services/DashboardPdvDesktopService";
import { SYSTEM_VARIABLE }           from "@/modules/Application";
import SyncPdvDesktopService         from "../services/SyncPdvDesktopService";
import TerminalConfigService          from "../services/TerminalConfigService";

// Paleta harmônica baseada no coral primário da Nuts (#ee7361)
const CHART_COLORS = [
  "#ee7361", "#e05a47", "#f5a394", "#f7bdb4", "#fad6d1",
  "#d0513d", "#b84330", "#c86a5a", "#f4c5bd", "#fde8e5",
];

export default {
  name: "PdvDashboard",

  filters: {
    numero(val) {
      const n = Number(val) || 0;
      return n.toLocaleString("pt-BR");
    },
  },

  data() {
    return {
      // estado de carregamento
      carregando:         true,
      carregandoGrafico:  true,

      // dados principais
      resumo: {
        produtos_total:       0,
        clientes_total:       0,
        nfces_total:          0,
        vendas_pendentes:     0,
        caixa_pendente:       0,
        ultima_sincronizacao: null,
        controles:            [],
      },
      terminal:              {},
      produtosMaisVendidos:  [],
      vendasPorGrupo:        [],

      // dados do modal de configurações (carregados ao abrir)
      dadosTerminal: { info: {}, emitente: null, configuracao: null, impressora: null },

      // estado de sync
      online:              navigator.onLine,
      sincronizando:       false,
      progressoSync:       0,
      statusSync:          "",
      sincronizandoFiscal: false,
      fazendoBackup:       false,
      // feedback
      msgSucesso: null,
      msgErro:    null,

      // modais
      modalSettings: false,
    };
  },

  computed: {
    chartSeries() {
      return this.produtosMaisVendidos.map((p) => Number(p.quantidade_total) || 0);
    },
    chartSeriesGrupos() {
      return [
        {
          name: "Quantidade",
          data: this.vendasPorGrupo.map((g) => Number(g.quantidade_total) || 0),
        },
      ];
    },
    chartOptions() {
      return {
        labels:  this.produtosMaisVendidos.map((p) => p.descricao || "—"),
        colors:  CHART_COLORS.slice(0, this.produtosMaisVendidos.length),
        legend:  { position: "bottom", fontSize: "12px", fontFamily: "inherit" },
        tooltip: {
          y: {
            formatter: (val) => `${Number(val).toLocaleString("pt-BR")} un.`,
          },
        },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: "62%",
              labels: {
                show:  true,
                total: {
                  show:      true,
                  label:     "Total",
                  formatter: (w) => {
                    const t = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                    return `${Number(t).toLocaleString("pt-BR")} un.`;
                  },
                },
              },
            },
          },
        },
        chart: { toolbar: { show: false }, animations: { enabled: true } },
      };
    },
    chartOptionsGrupos() {
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          animations: { enabled: true },
        },
        colors: ["#ee7361"],
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
            barHeight: "62%",
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.vendasPorGrupo.map((g) => g.grupo_descricao || "Sem grupo"),
          labels: {
            formatter: (val) => Number(val || 0).toLocaleString("pt-BR"),
          },
        },
        tooltip: {
          y: {
            formatter: (val) => `${Number(val || 0).toLocaleString("pt-BR")} un.`,
          },
        },
      };
    },
  },

  async mounted() {
    if (!this.$isDesktop) {
      this.$router.push({ name: "pdvDesktopLogin" });
      return;
    }
    window.addEventListener("online",  this._atualizarConexao);
    window.addEventListener("offline", this._atualizarConexao);
    window.addEventListener("pdv-dashboard-open-settings", this._abrirConfiguracoesViaHeader);
    window.addEventListener("pdv-dashboard-refresh", this._carregarTudo);
    await this._carregarTudo();

    // Ao abrir o PDV, reconcilia o status local das NFC-e com o back/web
    // (ex.: NFC-e cancelada no painel web). Fire-and-forget; nunca bloqueia a UI.
    SyncPdvDesktopService.sincronizarStatusNfces()
      .then((r) => {
        if (r && r.canceladas) this._carregarTudo();
      })
      .catch((e) => {
        console.warn("[PdvDashboard] reconciliação de status NFC-e:", e?.message ?? e);
      });
  },

  beforeDestroy() {
    window.removeEventListener("online",  this._atualizarConexao);
    window.removeEventListener("offline", this._atualizarConexao);
    window.removeEventListener("pdv-dashboard-open-settings", this._abrirConfiguracoesViaHeader);
    window.removeEventListener("pdv-dashboard-refresh", this._carregarTudo);
  },

  watch: {
    modalSettings(aberto) {
      if (aberto) this._carregarDadosTerminal();
    },
  },

  methods: {
    // ── Navegação ──────────────────────────────────────────────────────────
    irParaNovaVenda() {
      this.$router.push({ name: "pdvDesktopNovaVenda" });
    },
    irParaVendas() {
      this.$router.push({ name: "pdvDesktopVendasOffline" });
    },
    irParaImpressora() {
      this.modalSettings = false;
      this.$router.push({ name: "pdvDesktopConfiguracaoImpressao" });
    },

    // ── Carregamento ───────────────────────────────────────────────────────
    async _carregarTudo() {
      this.carregando        = true;
      this.carregandoGrafico = true;
      this.msgErro           = null;

      try {
        const [resumo, grafico, graficoGrupos] = await Promise.all([
          DashboardPdvDesktopService.obterResumo(),
          DashboardPdvDesktopService.obterProdutosMaisVendidosSemana(),
          DashboardPdvDesktopService.obterVendasPorGrupoSemana(),
        ]);

        this.resumo               = resumo;
        this.produtosMaisVendidos = grafico;
        this.vendasPorGrupo       = graficoGrupos;
        this.terminal             = this.$electron.terminal ? await this.$electron.terminal.getInfo() : {};
      } catch (e) {
        this.msgErro = `Erro ao carregar dados: ${e.message || e}`;
        console.error("[PdvDashboard]", e);
      } finally {
        this.carregando        = false;
        this.carregandoGrafico = false;
      }
    },

    async _carregarDadosTerminal() {
      try {
        this.dadosTerminal = await DashboardPdvDesktopService.obterDadosTerminal();
        this.terminal = this.dadosTerminal.info || this.terminal || {};
      } catch (e) {
        console.warn("[PdvDashboard] _carregarDadosTerminal:", e.message);
      }
    },

    // ── Conexão ────────────────────────────────────────────────────────────
    _atualizarConexao() {
      const eraOffline = !this.online;
      this.online = navigator.onLine;
      if (eraOffline && this.online) {
        // Ao recuperar internet, tenta transmitir NFC-e assinadas pendentes
        // (somente se o terminal ainda estiver autorizado pelo backend)
        if (!TerminalConfigService.isOnlineBloqueado()) {
          SyncPdvDesktopService.sincronizarNfcesAssinadas().catch((e) => {
            console.warn("[PdvDashboard] sync NFC-e assinadas:", e.message);
          });
        }
      }
    },

    _abrirConfiguracoesViaHeader() {
      this.modalSettings = true;
    },

    // ── Sincronização ──────────────────────────────────────────────────────
    async sincronizarAgora() {
      if (this.sincronizando) return;
      this.msgErro    = null;
      this.msgSucesso = null;
      this.sincronizando  = true;
      this.progressoSync  = 0;
      this.statusSync     = "Iniciando...";

      try {
        await SyncPdvDesktopService.sincronizarCompleto({
          onProgress: (pct, msg) => {
            this.progressoSync = pct;
            this.statusSync    = msg;
          },
        });

        this.msgSucesso = "Sincronização concluída.";
        setTimeout(() => { this.msgSucesso = null; }, 4000);
        await this._carregarTudo();
      } catch (e) {
        if (TerminalConfigService.isCodigoBloqueioEmpresa(e?.code)) {
          this.$router.push({ name: "pdvDesktopBloqueio" });
          return;
        }
        this.msgErro = `Erro na sincronização: ${e.message || e}`;
      } finally {
        this.sincronizando = false;
      }
    },

    async _sincronizarFiscal() {
      if (this.sincronizandoFiscal) return;
      this.sincronizandoFiscal = true;
      try {
        await FiscalContextPdvDesktopService.sincronizarContextoFiscal();
        await this._carregarDadosTerminal();
        this.msgSucesso = "Contexto fiscal sincronizado.";
        setTimeout(() => { this.msgSucesso = null; }, 3500);
      } catch (e) {
        if (TerminalConfigService.isCodigoBloqueioEmpresa(e?.code)) {
          this.$router.push({ name: "pdvDesktopBloqueio" });
          return;
        }
        this.msgErro = `Erro ao sincronizar fiscal: ${e.message || e}`;
      } finally {
        this.sincronizandoFiscal = false;
      }
    },

    async _fazerBackup() {
      if (this.fazendoBackup) return;
      this.msgSucesso = null;
      this.msgErro = null;

      if (!this.$electron || !this.$electron.backup) {
        this.msgErro = "Backup disponível apenas no aplicativo desktop.";
        return;
      }

      // Pergunta ao usuário em qual pasta deseja salvar o backup.
      let pasta = null;
      try {
        const escolha = await this.$electron.backup.selecionarPasta();
        if (!escolha || escolha.canceled) return; // usuário cancelou
        pasta = escolha.pasta || null;
      } catch (e) {
        this.msgErro = `Não foi possível abrir o seletor de pasta: ${e.message || e}`;
        return;
      }

      this.fazendoBackup = true;
      try {
        const resultado = await this.$electron.backup.executar({ pasta });
        const destino = resultado && resultado.destino ? resultado.destino : "";
        this.msgSucesso = destino
          ? `Backup concluído em: ${destino}`
          : "Backup concluído com sucesso.";
        setTimeout(() => { this.msgSucesso = null; }, 6000);
      } catch (e) {
        this.msgErro = `Erro ao fazer backup: ${e.message || e}`;
      } finally {
        this.fazendoBackup = false;
      }
    },

    async _recarregarTerminal() {
      await Promise.all([this._carregarTudo(), this._carregarDadosTerminal()]);
    },

    // ── Utilitários ────────────────────────────────────────────────────────
    _formatarData(dt) {
      if (!dt) return "Nunca";
      try {
        return new Date(dt).toLocaleString("pt-BR", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        });
      } catch { return dt; }
    },
    _nomeConfiguracaoNfce(configuracao) {
      if (!configuracao) return "—";
      let payload = {};
      try {
        payload = configuracao.payload_json ? JSON.parse(configuracao.payload_json) : {};
      } catch (_) {
        payload = {};
      }

      return (
        payload.descricao ||
        payload.nome ||
        payload.nome_configuracao ||
        configuracao.descricao ||
        configuracao.nome ||
        `Configuração #${configuracao.id_configuracao || configuracao.id || "—"}`
      );
    },
  },
};
</script>

<style scoped>
/* ── Base ─────────────────────────────────────────────────────────────────── */
.pdv-dash {
  width: 100%;
  max-width: none;
  padding: 16px;
  box-sizing: border-box;
  min-height: 100vh;
  background: #f5f7fa;
  font-family: inherit;
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.pdv-dash__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 6px rgba(0,0,0,.07);
}
.pdv-dash__header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pdv-dash__logo { max-height: 40px; }
.pdv-dash__title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: #1e293b;
}
.pdv-dash__subtitle {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
}
.pdv-dash__header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pdv-dash__badge-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}
.pdv-dash__badge-status--online  { background: #dcfce7; color: #166534; }
.pdv-dash__badge-status--offline { background: #fee2e2; color: #991b1b; }
.pdv-dash__status-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.pdv-dash__btn-gear {
  width: 36px; height: 36px;
  border: none;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  font-size: 1rem;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s;
}
.pdv-dash__btn-gear:hover { background: #e2e8f0; color: var(--primary-color, #ee7361); }

/* ── Cards ────────────────────────────────────────────────────────────────── */
.pdv-dash__cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}
.pdv-dash__card {
  background: #fff;
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 6px rgba(0,0,0,.06);
}
.pdv-dash__card-icon {
  width: 52px; height: 52px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}
.pdv-dash__card-icon--blue,
.pdv-dash__card-icon--green,
.pdv-dash__card-icon--indigo {
  background: #fde8e5;
  color: var(--primary-color, #ee7361);
}
.pdv-dash__card-info {
  display: flex; flex-direction: column;
  min-width: 0;
}
.pdv-dash__card-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #94a3b8;
  font-weight: 600;
}
.pdv-dash__card-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.1;
}
.pdv-dash__card-sub {
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Skeleton ─────────────────────────────────────────────────────────────── */
.pdv-dash__card--skeleton {
  pointer-events: none;
}
.pdv-dash__skeleton-icon {
  width: 52px; height: 52px;
  border-radius: 12px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: pdv-shimmer 1.4s infinite;
  flex-shrink: 0;
}
.pdv-dash__skeleton-lines { flex: 1; display: flex; flex-direction: column; gap: 8px; }
.pdv-dash__skeleton-line {
  border-radius: 4px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: pdv-shimmer 1.4s infinite;
}
.pdv-dash__skeleton-line--sm { height: 10px; width: 60%; }
.pdv-dash__skeleton-line--lg { height: 22px; width: 40%; }
@keyframes pdv-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Atalhos ──────────────────────────────────────────────────────────────── */
.pdv-dash__atalhos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}
.pdv-dash__atalho {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: filter .15s, opacity .15s;
  min-height: 44px;
}
.pdv-dash__atalho i { font-size: 1rem; }
.pdv-dash__atalho:disabled { opacity: .5; cursor: not-allowed; }
.pdv-dash__atalho--primary  { background: var(--primary-color, #ee7361); color: #fff; box-shadow: 0 2px 8px rgba(238,115,97,.25); }
.pdv-dash__atalho--primary:hover:not(:disabled) { filter: brightness(1.1); }
.pdv-dash__atalho--secondary {
  background: #fde8e5;
  color: var(--primary-color, #ee7361);
  border: 2px solid #f5a394;
  box-shadow: 0 1px 4px rgba(238,115,97,.12);
}
.pdv-dash__atalho--secondary:hover:not(:disabled) { background: #fad6d1; }
.pdv-dash__atalho--outline {
  background: #fff;
  color: var(--primary-color, #ee7361);
  border: 2px solid var(--primary-color, #ee7361);
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}
.pdv-dash__atalho--outline:hover:not(:disabled) { background: #fde8e5; }

/* ── Sync bar ─────────────────────────────────────────────────────────────── */
.pdv-dash__sync-bar {
  background: #fff;
  border-radius: 8px;
  padding: 10px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,.05);
}
.pdv-dash__sync-msg { font-size: 0.78rem; color: #64748b; margin-top: 4px; display: block; }

/* ── Alertas ──────────────────────────────────────────────────────────────── */
.pdv-dash__alert {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 12px;
}
.pdv-dash__alert--success { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
.pdv-dash__alert--danger  { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }

.pdv-fade-enter-active, .pdv-fade-leave-active { transition: opacity .3s; }
.pdv-fade-enter, .pdv-fade-leave-to { opacity: 0; }

/* ── Content row ──────────────────────────────────────────────────────────── */
.pdv-dash__content {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 1024px) {
  .pdv-dash__content {
    grid-template-columns: 1fr;
  }
}

/* ── Panel ────────────────────────────────────────────────────────────────── */
.pdv-dash__panel {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 6px rgba(0,0,0,.06);
  overflow: hidden;
}
.pdv-dash__panel-header {
  display: flex;
  align-items: center;
  padding: 12px 18px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #64748b;
  border-bottom: 1px solid #f1f5f9;
}
.pdv-dash__panel-body { padding: 16px; }

.pdv-dash__chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 240px;
}
.pdv-dash__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 220px;
  color: #94a3b8;
  font-size: 0.9rem;
  text-align: center;
}

/* ── Status list ──────────────────────────────────────────────────────────── */
.pdv-dash__status-list { display: flex; flex-direction: column; gap: 0; }
.pdv-dash__status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.85rem;
}
.pdv-dash__status-row:last-child { border-bottom: none; }
.pdv-dash__status-label { color: #64748b; }
.pdv-dash__status-value { color: #1e293b; font-weight: 600; }
.pdv-dash__status-value--mono { font-family: monospace; font-size: 0.78rem; color: #475569; }

/* Tags */
.pdv-dash__tag {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}
.pdv-dash__tag--green   { background: #dcfce7; color: #166534; }
.pdv-dash__tag--red     { background: #fee2e2; color: #991b1b; }
.pdv-dash__tag--orange  { background: #fff7ed; color: #c2410c; }
.pdv-dash__tag--neutral { background: #f1f5f9; color: #475569; }

/* ── Modal ────────────────────────────────────────────────────────────────── */
.pdv-dash__overlay {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, .50);
  display: flex; align-items: center; justify-content: center;
  z-index: 1100;
}
.pdv-dash__modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 580px;
  max-height: 88vh;
  display: flex; flex-direction: column;
  box-shadow: 0 16px 48px rgba(0,0,0,.22);
  overflow: hidden;
}
.pdv-dash__modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}
.pdv-dash__modal-title { margin: 0; font-size: 1rem; font-weight: 700; color: #1e293b; }
.pdv-dash__btn-close {
  background: none; border: none; font-size: 1.1rem;
  cursor: pointer; color: #94a3b8; padding: 4px;
  border-radius: 6px; line-height: 1;
}
.pdv-dash__btn-close:hover { background: #f1f5f9; color: #475569; }
.pdv-dash__modal-body {
  flex: 1; overflow-y: auto;
  padding: 18px 20px;
  display: flex; flex-direction: column; gap: 20px;
}
.pdv-dash__modal-footer {
  display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap;
  padding: 14px 20px;
  border-top: 1px solid #e2e8f0;
}

/* Settings sections */
.pdv-dash__settings-section-title {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #94a3b8;
  font-weight: 700;
  margin: 0 0 10px;
}
.pdv-dash__settings-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.pdv-dash__settings-table td {
  padding: 7px 4px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}
.pdv-dash__settings-table td:first-child { color: #64748b; width: 45%; }
.pdv-dash__settings-table td:last-child { color: #1e293b; font-weight: 600; }
.pdv-dash__settings-table tr:last-child td { border-bottom: none; }
.pdv-dash__settings-table code {
  font-size: 0.72rem;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  color: #475569;
  word-break: break-all;
}

/* Modal buttons */
.pdv-dash__modal-btn {
  border: none;
  border-radius: 7px;
  padding: 8px 16px;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex; align-items: center;
  transition: filter .15s;
}
.pdv-dash__modal-btn:disabled { opacity: .55; cursor: not-allowed; }
.pdv-dash__modal-btn--primary  { background: var(--primary-color, #ee7361); color: #fff; }
.pdv-dash__modal-btn--primary:hover:not(:disabled)  { filter: brightness(1.08); }
.pdv-dash__modal-btn--outline  { background: #fff; color: #475569; border: 1.5px solid #cbd5e1; }
.pdv-dash__modal-btn--outline:hover:not(:disabled)  { background: #f8fafc; }

/* ── text-primary helper ──────────────────────────────────────────────────── */
.text-primary { color: var(--primary-color, #ee7361) !important; }
</style>
